import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchStats } from "@/components/PublicStats";

// Counts smoothly from 0 to `target` (ease-out, ~1.8 s); static if reduced motion.
function useCountUp(target) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!target) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setV(target); return undefined; }
    let raf, t0;
    const step = (t) => {
      t0 = t0 || t;
      const p = Math.min(1, (t - t0) / 1800);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return v;
}

// Social proof for anonymous visitors: total summaries generated since launch.
export function SummariesCounter({ compact = false, testid = "summaries-counter" }) {
  const { t, i18n } = useTranslation();
  const [total, setTotal] = useState(null);
  useEffect(() => {
    let on = true;
    fetchStats().then((s) => on && s && setTotal(s.summaries_total));
    return () => { on = false; };
  }, []);
  const shown = useCountUp(total);
  if (!total) return null;
  const n = <strong className="font-mono font-bold text-[#002FA7]" data-testid={`${testid}-value`} data-target={total}>{shown.toLocaleString(i18n.language)}</strong>;
  if (compact) {
    return (
      <p data-testid={testid} className="mb-5 flex items-center justify-center gap-2 text-[13px] text-muted-foreground">
        <FileText className="w-4 h-4 text-[#002FA7]" /> {t("counter.already", "Déjà")} {n} {t("counter.since_launch", "résumés générés avec SUX1 depuis le lancement")}.
      </p>
    );
  }
  return (
    <div data-testid={testid} className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-[#002FA7]/40 bg-[#002FA7]/5 px-4 py-3">
      <span className="flex items-center gap-2 text-sm">
        <FileText className="w-4 h-4 text-[#002FA7]" /> {n} {t("counter.since_launch", "résumés générés avec SUX1 depuis le lancement")}
      </span>
      <Link to="/signup" data-testid={`${testid}-signup-link`} className="font-mono uppercase tracking-[0.14em] text-[11px] text-[#002FA7] hover:underline flex items-center gap-1">
        {t("counter.signup", "Créer mon compte gratuit")} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

export default SummariesCounter;
